/**
 * Перенос `domain/models/app_language.dart`.
 *
 * В Dart это `enum` с полями `param`, `name` и `locale`. Значением
 * перечисления здесь служит сам `param`: `toString()` в Dart возвращал
 * именно его, так что строковое представление языка не изменилось —
 * то, что лежит в хранилище и в адресе страницы, читается как раньше.
 */

export const AppLanguage = {
  uzbek: 'uz',
  english: 'en',
  russian: 'ru',
  arabic: 'ar',
} as const;

export type AppLanguage = (typeof AppLanguage)[keyof typeof AppLanguage];

/** Родные названия языков — один в один из Dart-перечисления. */
export const APP_LANGUAGE_NAME: Record<AppLanguage, string> = {
  [AppLanguage.uzbek]: "O'zbek",
  [AppLanguage.english]: 'English',
  [AppLanguage.russian]: 'Русский',
  [AppLanguage.arabic]: 'اَلْعَرَبِيَّةُ',
};

/**
 * `AppLanguage.values`. Порядок важен: сетка языков на экране выбора
 * рисуется прямо по нему (`const languages = AppLanguage.values`).
 */
export const APP_LANGUAGE_VALUES: readonly AppLanguage[] = [
  AppLanguage.uzbek,
  AppLanguage.english,
  AppLanguage.russian,
  AppLanguage.arabic,
];

/** `AppLanguage.supportedLocales` — в вебе локаль это тот же код языка. */
export const APP_LANGUAGE_SUPPORTED_LOCALES: readonly string[] = APP_LANGUAGE_VALUES;

export const defaultLanguage: AppLanguage = AppLanguage.uzbek;

/**
 * `AppLanguage.tryFromString`. В Dart первичным был `fromString`, а `try`
 * ловил его исключение; здесь наоборот — поведение то же, кода меньше.
 */
export function appLanguageTryFromString(param: string): AppLanguage | null {
  const found = APP_LANGUAGE_VALUES.find((value) => value === param);
  return found ?? null;
}

/** `AppLanguage.fromString` — неизвестный код это ошибка, как `firstWhere`. */
export function appLanguageFromString(param: string): AppLanguage {
  const language = appLanguageTryFromString(param);
  if (language === null) {
    throw new Error(`Bad state: no AppLanguage with param "${param}"`);
  }
  return language;
}

/**
 * Арабский — единственный язык с письмом справа налево, нужен для `dir="rtl"`.
 *
 * Важно: Flutter-приложение держало направление принудительно LTR
 * (`Directionality(textDirection: TextDirection.ltr)` в `appShellBuilder`),
 * поэтому ради вёрстки один в один корневой `dir` в порте тоже остаётся LTR.
 * Функция нужна там, где направление действительно потребуется точечно.
 */
export function isRtlLanguage(language: AppLanguage): boolean {
  return language === AppLanguage.arabic;
}
